import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Bean, createTranslatedText, NumberPatternType, Translate, ValidationRules } from '@dreryk/react-common';
import { FormLabel, StyledFlex } from '@dreryk/react-components';

import { getTreatmentDaysCountMinValue } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { DraftStandardPrescriptionFieldName } from '../../draftPrescriptions/standardPrescription/DraftStandardPrescriptionField.types';

import { StyledTreatmentDays } from './TreatmentDays.styled';
import { EMPTY_DISABLED_VALUE } from './TreatmentDaysNumberInput.constants';
import { StyledTreatmentDaysDescriptionText } from './TreatmentDaysDescriptionText.styled';
import { TreatmentDaysCountTooltip } from './TreatmentDaysCountTooltip';

type TreatmentDaysNumberInputProps = Pick<DraftPrescriptionsFormController, 'register' | 'trigger'> & {
   readonly name: DraftStandardPrescriptionFieldName;
   readonly defaultValue: number | null;
   readonly underTreatmentSupervision: boolean;
   readonly maxValue: number;
   readonly exceeded: boolean;
   readonly disabled: boolean;
};

export const TreatmentDaysNumberInput = (props: TreatmentDaysNumberInputProps): JSX.Element => {
   // Formularz nie ustawia `mode`, więc bez tego walidacja odpala się dopiero przy wysyłce (WYM-UI-4).
   const handleBlur = (): void => {
      props.trigger(props.name);
   };

   useUpdateEffect(() => {
      props.trigger(props.name);
   }, [props.exceeded, props.disabled, props.trigger, props.name]);

   const validationRules = useMemo<ValidationRules<Bean>>(
      () => ({
         required: props.underTreatmentSupervision && !props.disabled,
         max: props.underTreatmentSupervision
            ? { value: props.maxValue, message: createTranslatedText('') }
            : undefined,
         validate: () => !props.exceeded,
      }),
      [props.underTreatmentSupervision, props.maxValue, props.exceeded, props.disabled]
   );

   return (
      <StyledFlex direction={'column'} gap={'2px'}>
         <FormLabel
            testId={'treatmentDaysCount'}
            label={prescriptionTranslationKeys.numericalDosage.treatmentDaysCount}
            suffixIcon={<TreatmentDaysCountTooltip underTreatmentSupervision={props.underTreatmentSupervision} />}
            compact
            nowrap
         />
         <StyledFlex gap={'4px'} alignItems={'flex-start'}>
            <StyledTreatmentDays
               testId={'treatmentDays'}
               controller={props.register(props.name, validationRules)}
               min={getTreatmentDaysCountMinValue()}
               max={props.underTreatmentSupervision ? props.maxValue : undefined}
               pattern={NumberPatternType.POSITIVE_INTEGER}
               defaultValue={props.defaultValue}
               onBlur={handleBlur}
               disabled={props.disabled}
               placeholder={props.disabled ? EMPTY_DISABLED_VALUE : undefined}
               autoComplete={false}
               withoutRequiredMark
               compact
            />
            <StyledTreatmentDaysDescriptionText>
               <Translate>{prescriptionTranslationKeys.numericalDosage.days}</Translate>
            </StyledTreatmentDaysDescriptionText>
         </StyledFlex>
      </StyledFlex>
   );
};
